/**
 * Cart Preview Functionality
 * Shows what the cart total would be if the current variant were added
 * Updates automatically when variants change
 */

class CartPreview {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupCartPreview());
    } else {
      this.setupCartPreview();
    }
  }

  setupCartPreview() {
    // Check if we're on a product page
    if (!this.isProductPage()) {
      return;
    }

    // Get current cart data
    this.getCurrentCart().then(cart => {
      this.currentCart = cart;
      this.createCartPreviewElement();
      this.bindVariantChangeEvents();
      this.updateCartPreview();
    });
  }

  isProductPage() {
    return document.querySelector('.product-form') !== null;
  }

  async getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.log('Could not fetch cart:', error);
      return { items: [], item_count: 0, total_price: 0 };
    }
  }

  createCartPreviewElement() {
    // Look for existing cart preview
    let cartPreview = document.querySelector('.cart-preview');
    
    if (!cartPreview) {
      // Create cart preview element
      cartPreview = document.createElement('div');
      cartPreview.className = 'cart-preview';
      cartPreview.innerHTML = `
        <div class="cart-preview-content">
          <div class="cart-preview-item">
            <span class="cart-preview-label">Current Cart:</span>
            <span class="cart-preview-total money">${this.formatMoney(this.currentCart.total_price)}</span>
          </div>
          <div class="cart-preview-item">
            <span class="cart-preview-label">+ This Item:</span>
            <span class="cart-preview-variant-price money">$0.00</span>
          </div>
          <div class="cart-preview-item cart-preview-total-row">
            <span class="cart-preview-label">New Total:</span>
            <span class="cart-preview-new-total money">$0.00</span>
          </div>
        </div>
      `;

      // Insert after the price or before the add to cart button
      const priceElement = document.querySelector('.product-price__final, .product-price .money');
      const addToCartButton = document.querySelector('.add-to-cart-button');
      
      if (priceElement && addToCartButton) {
        priceElement.parentNode.insertBefore(cartPreview, addToCartButton.parentNode);
      } else if (addToCartButton) {
        addToCartButton.parentNode.parentNode.insertBefore(cartPreview, addToCartButton.parentNode);
      }
    }

    this.cartPreview = cartPreview;
  }

  bindVariantChangeEvents() {
    // Listen for variant changes on the main product select
    const productSelect = document.querySelector('.product-select');
    if (productSelect) {
      productSelect.addEventListener('change', () => this.updateCartPreview());
    }

    // Listen for swatch changes (color, fabric, etc.)
    const swatchInputs = document.querySelectorAll('.swatch-container input[type="radio"]');
    swatchInputs.forEach(input => {
      input.addEventListener('change', () => this.updateCartPreview());
    });

    // Listen for any other variant change events
    document.addEventListener('variant:change', () => this.updateCartPreview());
    
    // Also listen for the custom variant change event from your existing code
    document.addEventListener('variantChanged', () => this.updateCartPreview());
  }

  updateCartPreview() {
    if (!this.cartPreview) return;

    const currentVariant = this.getCurrentVariant();
    if (!currentVariant) return;

    const variantPrice = currentVariant.price;
    const currentCartTotal = this.currentCart.total_price;
    const newTotal = currentCartTotal + variantPrice;

    // Update the variant price display
    const variantPriceElement = this.cartPreview.querySelector('.cart-preview-variant-price');
    if (variantPriceElement) {
      variantPriceElement.textContent = this.formatMoney(variantPrice);
    }

    // Update the new total display
    const newTotalElement = this.cartPreview.querySelector('.cart-preview-new-total');
    if (newTotalElement) {
      newTotalElement.textContent = this.formatMoney(newTotal);
    }

    // Add visual feedback for price changes
    this.addPriceChangeAnimation(newTotalElement);
  }

  getCurrentVariant() {
    // Try to get variant from the product select
    const productSelect = document.querySelector('.product-select');
    if (productSelect && productSelect.value) {
      const variantId = productSelect.value;
      if (window.productVariants && window.productVariants[variantId]) {
        return window.productVariants[variantId];
      }
    }

    // Fallback: try to get from swatch selection
    const selectedOptions = this.getSelectedSwatchOptions();
    if (selectedOptions.length > 0 && window.productVariants) {
      // Find matching variant based on selected options
      for (const variantId in window.productVariants) {
        const variant = window.productVariants[variantId];
        if (this.variantMatchesOptions(variant, selectedOptions)) {
          return variant;
        }
      }
    }

    return null;
  }

  getSelectedSwatchOptions() {
    const selectedOptions = [];
    const swatchGroups = document.querySelectorAll('.swatch-container');
    
    swatchGroups.forEach(group => {
      const selectedRadio = group.querySelector('input[type="radio"]:checked');
      if (selectedRadio) {
        selectedOptions.push(selectedRadio.value);
      }
    });

    return selectedOptions;
  }

  variantMatchesOptions(variant, selectedOptions) {
    if (selectedOptions.length === 0) return false;
    
    // Check if variant matches the selected options
    // This is a simplified check - you may need to adjust based on your variant structure
    if (selectedOptions.length >= 1 && variant.option1 === selectedOptions[0]) {
      if (selectedOptions.length >= 2 && variant.option2 === selectedOptions[1]) {
        return true;
      } else if (selectedOptions.length === 1) {
        return true;
      }
    }
    
    return false;
  }

  addPriceChangeAnimation(element) {
    if (!element) return;
    
    element.style.transition = 'all 0.3s ease';
    element.style.transform = 'scale(1.1)';
    element.style.color = '#e74c3c';
    
    setTimeout(() => {
      element.style.transform = 'scale(1)';
      element.style.color = '';
    }, 300);
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  }

  // Method to refresh cart data (useful if cart changes from another page)
  refreshCartData() {
    this.getCurrentCart().then(cart => {
      this.currentCart = cart;
      this.updateCartPreview();
    });
  }
}

// Initialize cart preview
new CartPreview();

// Export for global access
window.CartPreview = CartPreview;
